import { Collection } from 'firestorter';

import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

/**
 * Canonical ingredient model backed by the Firestore `/ingredient` collection.
 *
 * @class Ingredient
 * @extends BaseDocument
 */
class Ingredient extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.INGREDIENT}/${id}`, opts);
  }

  /**
   * Get the name of the ingredient.
   * @returns {string}
   */
  get name() {
    return this.data.name || '';
  }

  /**
   * Get the category of the ingredient.
   * @returns {string}
   */
  get category() {
    return this.data.category || '';
  }

  /**
   * Get the allergen tags of the ingredient.
   * @returns {string[]}
   */
  get allergenTags() {
    return this.data.allergenTags || [];
  }

  /**
   * Get the calories per 100g of the ingredient.
   * @returns {number}
   */
  get caloriesPer100g() {
    return this.data.caloriesPer100g ?? null;
  }

  /**
   * Get the protein per 100g of the ingredient.
   * @returns {number}
   */
  get proteinPer100g() {
    return this.data.proteinPer100g ?? null;
  }

  /**
   * Get the carbs per 100g of the ingredient.
   * @returns {number}
   */
  get carbsPer100g() {
    return this.data.carbsPer100g ?? null;
  }

  /**
   * Get the fat per 100g of the ingredient.
   * @returns {number}
   */
  get fatPer100g() {
    return this.data.fatPer100g ?? null;
  }

  /**
   * Fetches the full canonical ingredient collection from Firestore and
   * returns each document as an `Ingredient` model instance. The caller is
   * responsible for caching the result for the lifetime it needs it.
   * @returns {Promise<Ingredient[]>} all canonical ingredients as model instances.
   */
  static async getAllIngredients() {
    const collection = new Collection(Collections.INGREDIENT, {
      createDocument: ({ id }, options) => new Ingredient(id, options),
    });
    await BaseDocument.initCollection(collection);
    return collection.docs;
  }
}

export default Ingredient;
